/**
 * WeeklyThemeBanner, dois modos:
 *  - mode="top" → faixa fina no topo (fixed) abaixo da PromoBar. Atualiza a
 *    CSS var `--weekly-banner-height` para a Navbar empilhar corretamente.
 *  - mode="section" → bloco editorial dentro de uma seção (devocional).
 * Aparece SOMENTE quando há tema ativo para hoje (/api/weekly-theme/current).
 */
import { useEffect, useRef } from "react";
import useWeeklyTheme from "@/hooks/useWeeklyTheme";

export function WeeklyThemeBanner({ mode = "section" }) {
  const { theme } = useWeeklyTheme();
  const ref = useRef(null);

  useEffect(() => {
    if (mode !== "top") return;
    const root = document.documentElement;
    if (!theme) {
      root.style.setProperty("--weekly-banner-height", "0px");
      return;
    }
    const measure = () => {
      const h = ref.current?.offsetHeight || 0;
      root.style.setProperty("--weekly-banner-height", h + "px");
    };
    measure();
    window.addEventListener("resize", measure);
    return () => {
      window.removeEventListener("resize", measure);
      root.style.setProperty("--weekly-banner-height", "0px");
    };
  }, [theme, mode]);

  if (!theme) return null;

  if (mode === "top") {
    return (
      <div
        ref={ref}
        data-testid="weekly-theme-banner-top"
        className="fixed inset-x-0 z-[55] text-white text-xs sm:text-sm tracking-wide"
        style={{
          top: "var(--promo-bar-height, 0px)",
          backgroundColor: theme.accent_color || "#0B1E36",
        }}
      >
        <div className="max-w-7xl mx-auto px-4 py-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 justify-center">
          <span className="inline-flex items-center gap-2">
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-white/80" />
            <span className="font-semibold uppercase">{theme.theme_name}</span>
          </span>
          {theme.verse_reference && (
            <span className="opacity-90 italic hidden md:inline">
              &ldquo;{theme.verse_text}&rdquo; · {theme.verse_reference}
            </span>
          )}
        </div>
      </div>
    );
  }

  // section variant
  return (
    <div
      data-testid="weekly-theme-banner-section"
      className="rounded-2xl p-5 sm:p-6 my-6 border"
      style={{
        backgroundColor: `${theme.accent_color || "#C5A059"}12`,
        borderColor: `${theme.accent_color || "#C5A059"}55`,
      }}
    >
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div className="flex-1">
          <p
            className="text-[11px] uppercase tracking-[0.18em] font-semibold mb-1"
            style={{ color: theme.accent_color || "#C5A059" }}
          >
            Série da semana
          </p>
          <h3 className="font-serif-display text-xl sm:text-2xl text-[#0B1E36]">
            {theme.theme_name}
          </h3>
          {theme.theme_description && (
            <p className="mt-2 text-sm text-[#0B1E36]/75 leading-relaxed">
              {theme.theme_description}
            </p>
          )}
        </div>
        {theme.verse_reference && (
          <div className="sm:max-w-[260px] sm:text-right">
            <p className="font-serif-display italic text-[#0B1E36]/85 text-[0.95rem] leading-snug">
              &ldquo;{theme.verse_text}&rdquo;
            </p>
            <p className="mt-1 text-xs uppercase tracking-wide text-[#0B1E36]/55">
              {theme.verse_reference}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export default WeeklyThemeBanner;
